/**
 * @fileoverview Customer Rewards tab — one card per company showing that
 * wash's Credits / Loyalty / Gifts & Promos (see RewardsLists), plus the
 * entry point for sending a gift. Credits and points are owner-scoped, so
 * there is no cross-company total here.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, RefreshControl, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import Card from '../../src/components/Card';
import Button from '../../src/components/Button';
import ContentContainer from '../../src/components/ContentContainer';
import RewardsLists from '../../src/components/RewardsLists';
import { api } from '../../src/api/client';
import { useTheme } from '../../src/context/ThemeContext';
import { useNetwork } from '../../src/context/NetworkContext';
import { spacing, fontSize, fontWeight, type ColorTheme } from '../../src/utils/theme';

interface UsedRedemption {
  id: string;
  redeemedAt: string;
  discountAmountCents: number;
  code: string;
  discountKind: 'percent' | 'free_wash' | 'free_minutes' | 'free_vend' | 'credit';
  percentOff: number | null;
  tierName: string | null;
  freeMinutes: number | null;
  freeVendCents?: number | null;
  creditCents?: number | null;
  ownerName: string;
}

function usedLabel(r: UsedRedemption): string {
  if (r.discountKind === 'credit') return r.creditCents != null ? `$${(r.creditCents / 100).toFixed(2)} credit` : 'Gift credit';
  // free_vend: the redemption's discountAmountCents (shown alongside) carries
  // the value when freeVendCents is absent.
  if (r.discountKind === 'free_vend') return r.freeVendCents != null ? `$${(r.freeVendCents / 100).toFixed(2)} vending credit` : 'Vending credit';
  if (r.discountKind === 'free_minutes') return `${r.freeMinutes ?? '?'} free minutes`;
  if (r.discountKind === 'free_wash' || r.percentOff === 100) return r.tierName ? `Free ${r.tierName} wash` : 'Free wash';
  return `${r.percentOff}% off${r.tierName ? ` ${r.tierName}` : ''}`;
}

export default function RewardsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const { isOffline } = useNetwork();

  const [isEmpty, setIsEmpty] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [used, setUsed] = useState<UsedRedemption[]>([]);
  const [usedTotal, setUsedTotal] = useState(0);
  const [usedPage, setUsedPage] = useState(1);
  const [usedExpanded, setUsedExpanded] = useState(false);

  const fetchUsed = useCallback((p = 1) => {
    api<{ redemptions: UsedRedemption[]; total: number }>(`/api/me/voucher-redemptions?page=${p}&limit=10`)
      .then(d => { setUsed(d.redemptions); setUsedTotal(d.total ?? d.redemptions.length); setUsedPage(p); })
      .catch(() => { /* history is a nicety */ });
  }, []);

  useEffect(() => { fetchUsed(1); }, [refreshKey, fetchUsed]);

  const handleLoaded = useCallback((d: { credits: unknown[]; points: unknown[]; vouchers: unknown[] }) => {
    setIsEmpty(d.credits.length === 0 && d.points.length === 0 && d.vouchers.length === 0);
  }, []);

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => {
        setRefreshing(true);
        // RewardsLists refetches on focus; bump its key to force a remount-fetch.
        setRefreshKey(k => k + 1);
        setTimeout(() => setRefreshing(false), 600);
      }} />}
    >
      <ContentContainer>
        <Button
          title="Send a Gift Wash"
          icon={<Ionicons name="gift" size={20} color={colors.white} />}
          onPress={() => router.push('/user/rewards/send-gift')}
          style={{ marginBottom: spacing.md }}
        />

        <RewardsLists key={refreshKey} onLoaded={handleLoaded} warnOnError={!isOffline} />

        {isEmpty && (
          <Card style={[styles.emptyCard, { justifyContent: 'center', padding: spacing.xl }]}>
            <Text style={{ color: colors.textSecondary, textAlign: 'center' }}>
              No rewards yet. Send a gift to someone, or watch for gifts and loyalty points from your favorite car wash.
            </Text>
          </Card>
        )}

        {used.length > 0 && (
          <Card style={{ marginTop: spacing.md }}>
            <TouchableOpacity
              onPress={() => setUsedExpanded(e => !e)}
              style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: colors.text }}>
                Promos & Gifts Used ({usedTotal})
              </Text>
              <Ionicons name={usedExpanded ? 'chevron-up' : 'chevron-down'} size={18} color={colors.textSecondary} />
            </TouchableOpacity>
            {usedExpanded && used.map(r => (
              <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: colors.borderLight, marginTop: spacing.sm }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium as any, color: colors.text }}>
                    {usedLabel(r)} · {r.ownerName}
                  </Text>
                  <Text style={{ fontSize: fontSize.xs, color: colors.textLight, marginTop: 2 }}>
                    {new Date(r.redeemedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} · code {r.code}
                  </Text>
                </View>
                {r.discountAmountCents > 0 && (
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any, color: colors.success }}>
                    -${(r.discountAmountCents / 100).toFixed(2)}
                  </Text>
                )}
              </View>
            ))}
            {usedExpanded && usedTotal > 10 && (
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.borderLight, marginTop: spacing.sm }}>
                <TouchableOpacity disabled={usedPage <= 1} onPress={() => fetchUsed(usedPage - 1)} hitSlop={8}>
                  <Ionicons name="chevron-back" size={20} color={usedPage <= 1 ? colors.border : colors.primary} />
                </TouchableOpacity>
                <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{usedPage}/{Math.max(1, Math.ceil(usedTotal / 10))}</Text>
                <TouchableOpacity disabled={usedPage >= Math.ceil(usedTotal / 10)} onPress={() => fetchUsed(usedPage + 1)} hitSlop={8}>
                  <Ionicons name="chevron-forward" size={20} color={usedPage >= Math.ceil(usedTotal / 10) ? colors.border : colors.primary} />
                </TouchableOpacity>
              </View>
            )}
          </Card>
        )}

        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  emptyCard: { flexDirection: 'row', alignItems: 'center', marginTop: spacing.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
